import { useQuery } from '@tanstack/react-query';
import type { LeadResult, LeadResultsResponse } from '../types/leads';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000';

export interface RecentActivity {
  id: number;
  address: string;
  city?: string;
  zip_code?: string;
  tier?: string;
  total_units?: number;
  zandi_composite_score?: number;
  created_at?: string;
  market_status?: string;
}

/**
 * Map LeadResult to RecentActivity, picking relevant fields
 */
function mapLeadToActivity(lead: LeadResult): RecentActivity {
  return {
    id: parseInt(lead.id, 10),
    address: lead.address,
    city: lead.neighborhood || undefined,
    zip_code: lead.zip_code || undefined,
    tier: lead.neighborhood_tier,
    total_units: lead.total_units,
    zandi_composite_score: lead.lead_score || undefined,
    created_at: undefined, // API doesn't return created_at in current response
    market_status: 'off-market', // Default value per spec
  };
}

/**
 * React Query hook to fetch recent activity for the home page.
 *
 * Fetches the 8 most recent leads sorted by created_at desc.
 * Uses the /api/leads endpoint with:
 * - sort_by=created_at
 * - sort_order=desc
 * - page_size=8
 * - page=1
 * - include_counts=false
 */
export function useRecentActivity() {
  const { data, isLoading, error } = useQuery<LeadResultsResponse>({
    queryKey: ['recent-activity'],
    queryFn: async () => {
      const params = new URLSearchParams({
        sort_by: 'created_at',
        sort_order: 'desc',
        page_size: '8',
        page: '1',
        include_counts: 'false',
      });

      const response = await fetch(`${API_BASE}/api/leads?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch recent activity');
      }
      return response.json();
    },
    staleTime: 3 * 60 * 1000, // 3 minutes
  });

  return {
    activities: data?.leads.map(mapLeadToActivity) ?? [],
    isLoading,
    error: error as Error | null,
  };
}
